import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ClockIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ArrowTrendingUpIcon,
  Cog6ToothIcon as SettingsIcon,
  MoonIcon,
  SunIcon,
  UserGroupIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { Soundwave } from 'react-bootstrap-icons';
import { ReactComponent as _OnyxLogoBlack } from '@/assets/images/ONYX-Logo-Black.svg';
import { ReactComponent as OnyxXLogo } from '@/assets/icons/ONYX - X - Dark Purple.svg';
import { useTheme, useLayout } from '@/contexts';
import { usePermissions } from '@/hooks';
import { UserMenu } from './UserMenu';

const navigation = [
  { name: 'Analysis History', href: '/dashboard', icon: ClockIcon },
  { name: 'Weibull Analysis', href: '/analysis', icon: ChartBarIcon },
  { name: 'VBL Analysis', href: '/vbl-analysis', icon: ChartBarSquareIcon },
  { name: 'Failure Forecasting', href: '/forecast', icon: ArrowTrendingUpIcon },
  { name: 'VBL Forecast', href: '/vbl-forecast', icon: Soundwave },
  { name: 'Sites', href: '/sites', icon: BuildingOfficeIcon },
  { name: 'VBL Config', href: '/vbl-config', icon: MapIcon },
];

const Sidebar: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { isLeftSidebarExpanded: isExpanded, setIsLeftSidebarExpanded: setIsExpanded } = useLayout();
  const permissions = usePermissions();
  const [showSettings, setShowSettings] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [leaveTimeout, setLeaveTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleSettingsClick = () => setShowSettings(!showSettings);

  const handleMouseEnter = () => {
    // Clear any pending collapse timeout
    if (leaveTimeout) {
      clearTimeout(leaveTimeout);
      setLeaveTimeout(null);
    }
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before collapsing to prevent accidental hiding
    const timeout = setTimeout(() => {
      setIsExpanded(false);
      setShowSettings(false); // Close settings when collapsing
      setHoveredItem(null);
      setLeaveTimeout(null);
    }, 150); // 150ms delay

    setLeaveTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeout) {
        clearTimeout(leaveTimeout);
      }
    };
  }, [leaveTimeout]);

  // Build navigation items based on permissions
  const navigationItems = [
    ...navigation,
    // Add Users menu item only for ONYX admins
    ...(permissions.canManageUsers()
      ? [{ name: 'Users', href: '/users', icon: UserGroupIcon }]
      : []),
  ];

  return (
    <div
      className={`fixed top-0 left-0 h-screen bg-white dark:bg-gray-900 shadow-sm border-r border-gray-200 dark:border-gray-700 flex flex-col z-40 hidden md:flex transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'w-64' : 'w-16'
        }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Top Section - Logo and Title */}
      <div className={`border-b border-gray-200 dark:border-gray-700 transition-all duration-300 ${isExpanded ? 'px-4 py-6' : 'px-2 py-6'}`}>
        <div className="flex items-center space-x-3">
          {/* Logo - always use ONYX X Logo */}
          <div className="flex-shrink-0 flex items-center justify-center" style={{ minWidth: '48px', minHeight: '32px' }}>
            <OnyxXLogo className="h-8 w-8" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
          {/* Title - properly hidden when collapsed */}
          {isExpanded && (
            <div className="transition-opacity duration-300 opacity-100">
              <h1 className="onyx-headline text-lg text-onyx-dark-blue dark:text-white leading-tight">
                ONYX<br />Foresight
              </h1>
            </div>
          )}
        </div>
      </div>

      {/* Middle Section - Navigation (grows to fill space with internal scroll) */}
      <nav className={`sidebar-nav flex-1 py-6 overflow-y-auto overflow-x-hidden transition-all duration-300 ${isExpanded ? 'px-4' : 'px-2'}`}>
        <ul role="list" className="space-y-2">
          {navigationItems.map((item) => (
            <li
              key={item.name}
              className="relative"
              onMouseEnter={() => !isExpanded && setHoveredItem(item.name)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2 text-sm font-fk-medium rounded-md transition-colors whitespace-nowrap overflow-hidden ${isActive
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-r-2 border-blue-700 dark:border-blue-300'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`
                }
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${isExpanded ? 'mr-3' : 'mx-auto'}`} />
                {/* Text properly hidden when collapsed */}
                {isExpanded && (
                  <span className="transition-opacity duration-300 opacity-100">
                    {item.name}
                  </span>
                )}
              </NavLink>

              {/* Tooltip for collapsed state */}
              {!isExpanded && hoveredItem === item.name && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 z-50">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-md py-1 px-2 whitespace-nowrap shadow-lg">
                    {item.name}
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section - User Info and Actions (always visible) */}
      <div className={`border-t border-gray-200 dark:border-gray-700 flex-shrink-0 transition-all duration-300 overflow-hidden ${isExpanded ? 'px-4 py-4' : 'px-2 py-4'}`}>
        <div className="space-y-3">
          {/* Settings Panel - Only show when expanded */}
          {showSettings && isExpanded && (
            <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="space-y-3">
                <h3 className="onyx-subtitle text-sm text-gray-700 dark:text-gray-300">Settings</h3>

                {/* Dark Mode Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {isDark ? (
                      <MoonIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <SunIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    )}
                    <span className="onyx-body text-sm text-gray-700 dark:text-gray-300">
                      {isDark ? 'Dark' : 'Light'} Theme
                    </span>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-onyx-bright-blue focus:ring-offset-2 ${isDark ? 'bg-onyx-bright-blue' : 'bg-gray-200'
                      }`}
                    role="switch"
                    aria-checked={isDark}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isDark ? 'translate-x-4' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Menu */}
          <div className={`flex ${isExpanded ? 'items-center justify-between' : 'flex-col items-center space-y-2'}`}>
            {isExpanded ? (
              <>
                <UserMenu onClickOutsideParent={setIsExpanded} />
                <button
                  onClick={handleSettingsClick}
                  className={`p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-onyx-bright-blue focus:ring-offset-1 rounded ${showSettings
                    ? 'text-onyx-bright-blue'
                    : 'text-gray-400 dark:text-gray-500 hover:text-onyx-bright-blue dark:hover:text-onyx-bright-blue'
                    }`}
                  aria-label="Settings"
                >
                  <SettingsIcon className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex justify-center">
                  <UserMenu collapsed={true} onClickOutsideParent={setIsExpanded} />
                </div>
                <button
                  onClick={handleSettingsClick}
                  className={`p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-onyx-bright-blue focus:ring-offset-1 rounded ${showSettings
                    ? 'text-onyx-bright-blue'
                    : 'text-gray-400 dark:text-gray-500 hover:text-onyx-bright-blue dark:hover:text-onyx-bright-blue'
                    }`}
                  aria-label="Settings"
                >
                  <SettingsIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
